/**
 * Rain (and watering) amounts in the gardener's units.
 *
 * The outlook and the forecast carry every amount in inches. A gardener who
 * reads temperatures in °C reads rain in millimetres, so the one choice in
 * the profile, the temperature unit, decides both: °F shows inches ("1.5″"),
 * °C shows millimetres ("38 mm"). Every place that shows an amount of rain
 * or water says it through formatRain, so they never disagree.
 *
 * Millimetres are rounded to whole ones, or to a tenth under 10 mm, where a
 * millimetre either way is a good part of the amount.
 *
 * Who uses it: Today, the Weather page's week, the plant card's readings
 * and About sheet, and the Plants page's conditions bar.
 *
 * @module rainUtils
 */

/** Millimetres in an inch. */
export const MM_PER_INCH = 25.4;

/** Under this many millimetres an amount keeps a tenth: "4.6 mm", not "5 mm". */
const MM_TENTHS_UNDER = 10;

/** Which unit rain is shown in: inches, or millimetres. */
export type RainUnit = 'in' | 'mm';

/** How formatRain says an amount. */
export interface RainFormatOptions {
  /**
   * Without the space before "mm" ("38mm"), for a narrow cell such as the
   * plant card's rain row. Inches have no space either way.
   */
  compact?: boolean;
  /** Round inches to this many decimals; left as given when absent. */
  inchDigits?: number;
  /** Keep the trailing zeros of inchDigits: "1.0″" rather than "1″". */
  fixed?: boolean;
}

/**
 * The rain unit that goes with a temperature unit: millimetres with Celsius,
 * inches with anything else.
 *
 * @param temperatureUnit - The gardener's unit: 'C' or 'F' from the profile, 'c' or 'f' from the outlook.
 * @returns 'mm' for Celsius, else 'in'.
 */
export function rainUnitFor(temperatureUnit: string | null | undefined): RainUnit {
  return temperatureUnit === 'C' || temperatureUnit === 'c' ? 'mm' : 'in';
}

/**
 * An amount in inches as the outlook sends it (a number, or a string such as
 * "1.5"), as a number.
 *
 * @param value - The amount, in inches.
 * @returns The number, or 0 when there is none or it cannot be read.
 */
const toInches = (value: number | string | null | undefined): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Inches as millimetres, rounded as they are shown: whole millimetres, or a
 * tenth under 10 mm.
 *
 * @param inches - The amount, in inches (a number, or a string such as "1.5").
 * @returns The millimetres, rounded.
 */
export function inchesToMm(inches: number | string | null | undefined): number {
  const mm = toInches(inches) * MM_PER_INCH;
  return mm < MM_TENTHS_UNDER ? Math.round(mm * 10) / 10 : Math.round(mm);
}

/**
 * An amount of rain or water in the gardener's unit: "1.5″", or "38 mm".
 *
 * @param inches - The amount, in inches (a number, or a string such as "1.5"; nothing reads as 0).
 * @param temperatureUnit - The gardener's temperature unit ('C'/'F', or the outlook's 'c'/'f'); Celsius shows millimetres.
 * @param options - Compact millimetres, and how to round inches.
 * @returns The amount with its unit.
 */
export function formatRain(
  inches: number | string | null | undefined,
  temperatureUnit: string | null | undefined,
  options: RainFormatOptions = {},
): string {
  const value = toInches(inches);
  if (rainUnitFor(temperatureUnit) === 'mm') {
    return `${inchesToMm(value)}${options.compact ? '' : ' '}mm`;
  }
  if (options.inchDigits === undefined) return `${value}″`;
  if (options.fixed) return `${value.toFixed(options.inchDigits)}″`;
  const scale = 10 ** options.inchDigits;
  return `${Math.round(value * scale) / scale}″`;
}
